import { useState, useEffect } from 'react';
import './CreatePersonalFeed.css';
import { personalFeedAPI, eventsAPI, picturesAPI } from './services/api';
import { parseUTCDate } from './utils/dateUtils';

function CreatePersonalFeed({ currentUser, onFeedCreated }) {
  const [description, setDescription] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [selectedPictureId, setSelectedPictureId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [userPictures, setUserPictures] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    const loadUserData = async () => {
      try {
        // Load user's pictures
        const pictures = await picturesAPI.getUserPictures(currentUser.id);
        setUserPictures(pictures);

        // Load user's past events
        const events = await eventsAPI.getUserPastEvents(currentUser.id);
        setUserEvents(events);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim() && !externalLink.trim() && !selectedPictureId && !selectedEventId) {
      alert('Please add a description or at least one other field');
      return;
    }

    try {
      setLoading(true);

      const feedData = {
        description: description.trim() || null,
        externalLink: externalLink.trim() || null,
        pictureId: selectedPictureId,
        eventId: selectedEventId
      };

      await personalFeedAPI.create(feedData);

      // Reset form
      setDescription('');
      setExternalLink('');
      setSelectedPictureId(null);
      setSelectedEventId(null);
      setShowOptionalFields(false);

      // Notify parent component
      if (onFeedCreated) {
        onFeedCreated();
      }

      alert('Post created successfully!');
    } catch (error) {
      console.error('Error creating feed:', error);
      const errorMessage = error.response?.data || 'Failed to create post. Please try again.';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="create-personal-feed compact">
      <form onSubmit={handleSubmit} className="feed-form compact">
        <div className="main-input-group">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What's on your mind?"
            className="feed-description-input"
            rows="2"
          />

          <div className="form-actions">
            <button
              type="button"
              className="toggle-options-btn"
              onClick={() => setShowOptionalFields(!showOptionalFields)}
            >
              {showOptionalFields ? '− Less' : '+ More'}
            </button>
            <button
              type="submit"
              className="submit-feed-btn compact"
              disabled={loading}
            >
              {loading ? 'Posting...' : 'Post'}
            </button>
          </div>
        </div>

        {showOptionalFields && (
          <div className="optional-fields">
            <div className="form-group compact">
              <label>Link</label>
              <input
                type="url"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                placeholder="https://example.com"
                className="feed-link-input"
              />
            </div>

            <div className="form-group compact">
              <label>Picture</label>
              <select
                value={selectedPictureId || ''}
                onChange={(e) => setSelectedPictureId(e.target.value ? parseInt(e.target.value) : null)}
                className="feed-select"
              >
                <option value="">-- None --</option>
                {userPictures.map(picture => (
                  <option key={picture.id} value={picture.id}>
                    {picture.picUserName} ({picture.eventName || 'No event'})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group compact">
              <label>Event</label>
              <select
                value={selectedEventId || ''}
                onChange={(e) => setSelectedEventId(e.target.value ? parseInt(e.target.value) : null)}
                className="feed-select"
              >
                <option value="">-- None --</option>
                {userEvents.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name} - {parseUTCDate(event.dateTime).toLocaleDateString('bg-BG')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default CreatePersonalFeed;
